/* Asset Supply — two live systems.
   1) BENTO: one halftone field fixed in grid space; cells cross-fade a window
      onto it. Nothing travels between cells — the field is still, the reveal moves.
   2) FOCAL TYPE: headings blur by distance from the cursor. */

/* ─────────────────────────── 1 · field + reveal ───────────────────────────── */
function liveBento() {
  const grid = document.querySelector('.as-root .bento');
  if (!grid) return;
  const cells = [...grid.querySelectorAll('.cell')];
  if (!cells.length) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DOT = getComputedStyle(document.documentElement)
                .getPropertyValue('--surface').trim() || '#f7f7f7';
  const PITCH = 15, MAXR = 4.6;

  // one offscreen field, grid-sized; every cell window blits from it
  const buf = document.createElement('canvas');
  const bx = buf.getContext('2d');
  let gw = 0, gh = 0, dpr = 1;

  const plates = cells.map(cell => {
    const p = document.createElement('div');
    p.className = 'cell__plate';
    const c = document.createElement('canvas');
    p.append(c); cell.prepend(p);
    return { cell, plate: p, cv: c, cx: c.getContext('2d'), x: 0, y: 0, w: 0, h: 0 };
  });

  const layout = () => {
    const gr = grid.getBoundingClientRect();
    dpr = Math.min(devicePixelRatio || 1, 2);
    gw = gr.width; gh = gr.height;
    buf.width = Math.round(gw * dpr); buf.height = Math.round(gh * dpr);
    bx.setTransform(dpr, 0, 0, dpr, 0, 0);
    for (const p of plates) {
      const r = p.cell.getBoundingClientRect();
      p.x = r.left - gr.left; p.y = r.top - gr.top;
      p.w = r.width; p.h = r.height;
      p.cv.width = Math.round(r.width * dpr);
      p.cv.height = Math.round(r.height * dpr);
      p.cv.style.width = r.width + 'px';
      p.cv.style.height = r.height + 'px';
    }
  };

  let t = 0, raf = null;
  const frame = () => {
    t += 0.016;
    if (gw && gh) {
      bx.clearRect(0, 0, gw, gh);
      bx.fillStyle = DOT;
      for (let y = PITCH / 2; y < gh; y += PITCH) {
        for (let x = PITCH / 2; x < gw; x += PITCH) {
          const u = x / gw, v = y / gh;
          const a = Math.sin(u * 8.0 - t * 0.95);
          const b = Math.sin(v * 3.6 + t * 0.62);
          const c = Math.sin((u + v) * 4.4 - t * 0.34);
          let f = 0.5 + 0.5 * ((a + b + c) / 3);
          const r = f * f * MAXR;
          if (r < 0.4) continue;
          bx.beginPath(); bx.arc(x, y, r, 0, 6.283); bx.fill();
        }
      }
      // blit only the windows that are actually visible
      for (const p of plates) {
        if (p.plate.dataset.on !== '1' && p.plate.style.opacity === '') continue;
        p.cx.setTransform(1, 0, 0, 1, 0, 0);
        p.cx.clearRect(0, 0, p.cv.width, p.cv.height);
        p.cx.drawImage(buf, p.x * dpr, p.y * dpr, p.w * dpr, p.h * dpr,
                            0, 0, p.w * dpr, p.h * dpr);
      }
    }
    raf = requestAnimationFrame(frame);
  };

  let i = Math.max(0, cells.findIndex(c => c.classList.contains('cell--signal')));
  let timer = null, held = false;

  const show = n => {
    plates.forEach((p, k) => {
      const on = k === n;
      // asymmetric handoff: the outgoing cell clears faster than the incoming
      // arrives, so there is no long stretch where two cells read as active
      p.plate.style.transitionDuration = on ? '300ms' : '150ms';
      p.plate.style.transitionTimingFunction = on
        ? 'cubic-bezier(.16,1,.3,1)'      // easeOutExpo in
        : 'cubic-bezier(.4,0,1,1)';       // easeIn out
      p.plate.dataset.on = on ? '1' : '0';
      p.plate.style.opacity = on ? '1' : '0';
      p.cell.classList.toggle('is-live', on);
    });
    i = n;
  };
  const advance = () => show((i + 1) % cells.length);
  const play = () => { if (!reduce && !held && !timer) timer = setInterval(advance, 1000); };
  const pause = () => { clearInterval(timer); timer = null; };

  cells.forEach((cell, n) => {
    cell.addEventListener('pointerenter', () => { held = true; pause(); show(n); });
    cell.addEventListener('pointerleave', () => { held = false; play(); });
  });
  grid.addEventListener('pointerleave', () => { held = false; play(); });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { pause(); cancelAnimationFrame(raf); raf = null; }
    else { if (!raf) frame(); play(); }
  });
  addEventListener('resize', layout);

  grid.classList.add('is-enhanced');
  layout(); show(i); frame(); play();
}

/* ── Get Pro: yellow plate that only wakes on hover ───────────────────────── */
function plateButtons() {
  const targets = [...document.querySelectorAll('.as-root .panel .btn, .as-root .bleed .btn')];
  if (!targets.length) return;
  if (matchMedia('(hover: none)').matches) return;      // pointer only, no idle motion on touch

  for (const btn of targets) {
    btn.classList.add('btn--plate');
    const c = document.createElement('canvas');
    btn.prepend(c);
    const cx = c.getContext('2d');
    let t = 0, raf = null, on = false;

    const size = () => {
      const r = btn.getBoundingClientRect(), d = Math.min(devicePixelRatio || 1, 2);
      if (!r.width) return;
      c.width = Math.round(r.width * d); c.height = Math.round(r.height * d);
      cx.setTransform(d, 0, 0, d, 0, 0); c._w = r.width; c._h = r.height;
    };
    const frame = () => {
      const w = c._w, h = c._h; t += 0.02;
      if (w && h) {
        cx.clearRect(0, 0, w, h);
        cx.fillStyle = 'rgba(20,20,20,.30)';           // ink dots ON the yellow
        const P = 8;
        for (let y = P / 2; y < h; y += P) {
          for (let x = P / 2; x < w; x += P) {
            const u = x / w, v = y / h;
            let f = 0.5 + 0.5 * ((Math.sin(u * 11 - t * 2.0) + Math.sin(v * 5 + t * 1.2)) / 2);
            const r = f * f * 2.3;
            if (r < 0.35) continue;
            cx.beginPath(); cx.arc(x, y, r, 0, 6.283); cx.fill();
          }
        }
      }
      raf = requestAnimationFrame(frame);
    };
    btn.addEventListener('pointerenter', () => {
      if (on) return; on = true; size(); if (!raf) frame();
    });
    btn.addEventListener('pointerleave', () => {
      on = false; cancelAnimationFrame(raf); raf = null;
    });
    addEventListener('resize', () => { if (on) size(); });
  }
}

addEventListener('DOMContentLoaded', () => { liveBento(); plateButtons(); });
